import { useCallback, useEffect, useState, useRef } from 'react';
import {
  usePlaidLink,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkError,
  PlaidLinkOptions,
} from 'react-plaid-link';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  selectReviewOnboardSlice,
  getPlaidLinkToken,
  getPlaidProcessorToken,
  consumePlaidLinkToken,
  setPlaidDialogState,
  setPlaidLinkToken,
  setPlaidShouldOpenWhenReady,
  linkPlaidBankAccountSecure,
  fetchPlaidLinkedBankAccounts,
  updatePlaidLinkedBankAccountStatusSecure,
  PlaidLinkedBankAccountSafe,
} from 'app/pages/store/reviewOnboardSlice';
import { useAuth } from 'app/shared-components/auth/AuthProvider';
import { captureException } from '@sentry/react';
import { addExternalBankAccountViaPlaid, getFinancialAccountDetails } from 'app/pages/store/fundingSlice';
import { HNExternalFinancialBankAccount } from 'API';
import { BankLinkingDialogState } from 'app/pages/cards/credit/BankAccountLinkingDialogs';
import { logCreditAuditEvent } from 'app/pages/cards/credit/creditAuditLog';

// Type for account from Plaid Link metadata
type PlaidAccount = PlaidLinkOnSuccessMetadata['accounts'][number];

/**
 * How long a user has to enter the micro-deposit code before the pending account
 * can no longer be verified. Single source of truth: the duplicate check and the
 * pending side sheet must agree, otherwise the UI tells users to re-add an account
 * that the duplicate check still refuses (SEE-1445).
 */
export const MICRO_DEPOSIT_VERIFICATION_DAYS = 14;

/**
 * True once a pending micro-deposit account is past its verification window.
 * Compares real timestamps rather than whole days so an account with hours left
 * is not treated as dead a day early.
 */
export function isMicroDepositExpired(initiatedAt: string): boolean {
  return moment(initiatedAt).add(MICRO_DEPOSIT_VERIFICATION_DAYS, 'days').isSameOrBefore(moment());
}

/**
 * Whole days left to verify, rounded up so a partial day reads as "1 day left"
 * rather than "0 days left" while the account is still verifiable.
 */
export function getMicroDepositDaysRemaining(initiatedAt: string): number {
  const hoursLeft = moment(initiatedAt)
    .add(MICRO_DEPOSIT_VERIFICATION_DAYS, 'days')
    .diff(moment(), 'hours', true);
  return Math.max(0, Math.ceil(hoursLeft / 24));
}

/**
 * Represents a bank account linked via Plaid (stored in DynamoDB).
 * This is used for both pending and verified accounts.
 * NOTE: plaidAccessToken is stored server-side only and NEVER exposed to frontend.
 */
export interface PlaidLinkedBankAccount {
  id: string;
  paidolId: string;
  plaidItemId: string;
  plaidAccountId: string;
  highnoteAccountId?: string;
  accountName: string;
  accountMask: string;
  accountType: string;
  verificationMethod: 'instant' | 'micro_deposit';
  status: 'pending' | 'active' | 'closed';
  linkedAt: string;
  verifiedAt?: string;
  closedAt?: string;
  failureReason?: string;
}

/**
 * Legacy interface for backward compatibility with components that use the old naming.
 * Maps to PlaidLinkedBankAccount with status='pending'.
 * NOTE: plaidAccessToken is no longer exposed - it stays on the backend.
 */
export interface PendingBankAccount {
  id: string;
  paidolId: string;
  plaidItemId: string;
  plaidAccountId: string;
  accountName: string;
  accountMask: string;
  accountType: string;
  verificationStatus: string;
  initiatedAt: string;
  verifiedAt?: string;
  failureReason?: string;
}

/**
 * Convert a PlaidLinkedBankAccountSafe to the legacy PendingBankAccount format.
 */
export function toPendingBankAccount(linked: PlaidLinkedBankAccountSafe): PendingBankAccount {
  return {
    id: linked.id,
    paidolId: linked.paidolId,
    plaidItemId: linked.plaidItemId,
    plaidAccountId: linked.plaidAccountId,
    accountName: linked.accountName,
    accountMask: linked.accountMask,
    accountType: linked.accountType,
    verificationStatus: linked.status === 'pending' ? 'pending_manual_verification' : linked.status,
    initiatedAt: linked.linkedAt,
    verifiedAt: linked.verifiedAt,
    failureReason: linked.failureReason,
  };
}

export interface UsePlaidBankAccountLinkOptions {
  /** Called when bank accounts are successfully added to Highnote (instantly verified) */
  onSuccess?: (accounts: HNExternalFinancialBankAccount[]) => void;
  /** Called when pending accounts are created (micro-deposit flow) */
  onPendingCreated?: (pendingAccounts: PendingBankAccount[]) => void;
  /** Called when the flow fails at any step */
  onError?: (error: Error) => void;
  /** Called when user exits Plaid Link without completing */
  onExit?: () => void;
  /** Existing pending accounts to check for duplicates */
  existingPendingAccounts?: PendingBankAccount[];
  /** Existing active accounts to check for duplicates (mask + type format) */
  existingActiveAccounts?: Array<{ mask?: string; type?: string }>;
}

// Highnote only supports these account types
const SUPPORTED_ACCOUNT_TYPES = ['checking', 'savings'];

export interface UsePlaidBankAccountLinkResult {
  /** Opens the Plaid Link modal */
  openPlaidLink: () => void;
  /** Whether Plaid Link is ready to open */
  isReady: boolean;
  /** Whether the flow is currently in progress (loading token, exchanging, adding to HN) */
  isLoading: boolean;
  /** Current step of the flow for UI feedback */
  currentStep:
    | 'idle'
    | 'loading-token'
    | 'plaid-open'
    | 'exchanging'
    | 'adding-to-hn'
    | 'saving-pending'
    | 'success'
    | 'error';
  /** Dialog state for showing confirmation dialogs */
  dialogState: BankLinkingDialogState;
  /** Close the dialog */
  closeDialog: () => void;
}

/**
 * Custom hook that handles the complete Plaid bank account linking flow:
 *
 * For Instant Auth:
 * 1. Generate Plaid Link token
 * 2. Open Plaid Link modal (user authenticates)
 * 3. Exchange public_token for processor_token
 * 4. Add bank account to Highnote
 * 5. Save record to PlaidLinkedBankAccount table
 * 6. Refresh account list
 *
 * For Micro-deposits (Same Day):
 * 1. Generate Plaid Link token
 * 2. Open Plaid Link modal (user enters account/routing numbers)
 * 3. Exchange public_token for access_token (NOT processor_token)
 * 4. Store pending account in PlaidLinkedBankAccount table
 * 5. User returns later to verify micro-deposit code
 */
export function usePlaidBankAccountLink(
  options: UsePlaidBankAccountLinkOptions = {}
): UsePlaidBankAccountLinkResult {
  const {
    onSuccess,
    onPendingCreated,
    onError,
    onExit,
    existingPendingAccounts = [],
    existingActiveAccounts = [],
  } = options;

  // Use refs to avoid stale closures in callbacks captured by usePlaidLink
  // Update refs SYNCHRONOUSLY during render (not in useEffect) to ensure they have current values
  const existingPendingAccountsRef = useRef(existingPendingAccounts);
  const existingActiveAccountsRef = useRef(existingActiveAccounts);
  existingPendingAccountsRef.current = existingPendingAccounts;
  existingActiveAccountsRef.current = existingActiveAccounts;

  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { integration } = useAppSelector(selectReviewOnboardSlice);

  // Use ref for integration to prevent usePlaidLink from reinitializing when integration changes
  const integrationRef = useRef(integration);
  integrationRef.current = integration;

  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<UsePlaidBankAccountLinkResult['currentStep']>('idle');

  // LOCAL token state - this controls what usePlaidLink sees
  // We use local state (not Redux) to have precise control over the null → token transition
  const [localPlaidToken, setLocalPlaidToken] = useState<string | null>(null);

  // Get Plaid state from Redux store (persists across component remounts)
  const { plaidDialogState, plaidLinkToken, plaidShouldOpenWhenReady } =
    useAppSelector(selectReviewOnboardSlice);

  // Use Redux flag for shouldOpenWhenReady - this persists across remounts
  const shouldOpenWhenReady = plaidShouldOpenWhenReady || false;

  // Sync Redux token to local state ONLY when component mounts with an existing token
  // This handles the onboarding remount case
  useEffect(() => {
    if (plaidLinkToken?.link_token && shouldOpenWhenReady && !localPlaidToken) {
      setLocalPlaidToken(plaidLinkToken.link_token);
    }
  }, [plaidLinkToken?.link_token, shouldOpenWhenReady, localPlaidToken]);

  // Dialog state from Redux (defaults to closed if not set)
  const dialogState: BankLinkingDialogState = plaidDialogState || { type: 'closed' };

  // Helper to update dialog state in Redux
  const setDialogState = useCallback(
    (state: BankLinkingDialogState) => {
      dispatch(setPlaidDialogState(state));
    },
    [dispatch]
  );

  const closeDialog = useCallback(() => {
    dispatch(setPlaidDialogState({ type: 'closed' }));
  }, [dispatch]);

  /**
   * Check if an account is pending manual verification (micro-deposit flow)
   */
  const isPendingVerification = (account: PlaidAccount): boolean => {
    return account.verification_status === 'pending_manual_verification';
  };

  /**
   * Close pending accounts whose verification window has lapsed so they stop being
   * returned by the pending query. Best effort: a failure here must not block the
   * link the user is in the middle of, since the in-memory filter already unblocks them.
   */
  const closeExpiredPendingAccounts = useCallback(
    async (expired: PendingBankAccount[]): Promise<void> => {
      await Promise.all(
        expired.map(async (account) => {
          try {
            await dispatch(
              updatePlaidLinkedBankAccountStatusSecure({
                id: account.id,
                status: 'closed',
                failureReason: 'Micro-deposit verification window expired',
              })
            ).unwrap();
          } catch (closeError) {
            captureException(closeError);
          }
        })
      );
    },
    [dispatch]
  );

  /**
   * Check if an account is a duplicate.
   * Fetches existing accounts fresh from the store to avoid stale closure issues.
   */
  const isDuplicateAccount = useCallback(
    (
      account: PlaidAccount,
      freshPendingAccounts: PendingBankAccount[],
      freshActiveAccounts: Array<{ mask?: string; type?: string }>
    ): { isDuplicate: boolean; reason?: string } => {
      const accountMask = account.mask || '';
      const accountType = account.subtype?.toLowerCase() || '';

      // Check against existing pending accounts
      const pendingDuplicate = freshPendingAccounts.find(
        (p) => p.accountMask === accountMask && p.accountType.toLowerCase() === accountType
      );
      if (pendingDuplicate) {
        return { isDuplicate: true, reason: 'This account is already pending verification' };
      }

      // Check against existing active accounts
      const activeDuplicate = freshActiveAccounts.find(
        (a) => a.mask === accountMask && a.type?.toLowerCase() === accountType
      );
      if (activeDuplicate) {
        return { isDuplicate: true, reason: 'This account is already linked' };
      }

      return { isDuplicate: false };
    },
    []
  );

  /**
   * Store a linked bank account record securely via the backend.
   * The access_token is exchanged and stored server-side - never exposed to frontend.
   */
  const storeLinkedAccountSecure = async (
    publicToken: string,
    account: PlaidAccount,
    paidolId: string,
    verificationMethod: 'instant' | 'micro_deposit',
    highnoteAccountId?: string
  ): Promise<PlaidLinkedBankAccountSafe> => {
    const result = await dispatch(
      linkPlaidBankAccountSecure({
        publicToken,
        accountId: account.id,
        accountType: account.subtype?.toLowerCase() || 'checking',
        accountName: account.name || `Account ending in ${account.mask}`,
        accountMask: account.mask || '****',
        paidolId,
        verificationMethod,
        highnoteAccountId,
      })
    ).unwrap();

    if (!result) {
      throw new Error('Failed to save linked account');
    }
    return result;
  };

  /**
   * Handle successful Plaid Link completion.
   *
   * This function orchestrates the complete post-Plaid-Link flow:
   *
   * 1. VALIDATION
   *    - Filter accounts to supported types (checking/savings only)
   *    - Validate integration exists
   *
   * 2. DUPLICATE DETECTION
   *    - Fetch fresh existing accounts from backend (pending + active)
   *    - Compare selected accounts against existing to prevent duplicates
   *    - Note: If fetch fails, we continue with empty arrays to avoid blocking the user
   *
   * 3. PROCESS INSTANT-AUTH ACCOUNTS
   *    For each instantly-verified account:
   *    - Exchange public_token for processor_token via Plaid API
   *    - Add bank account to Highnote using processor_token
   *    - Store record in PlaidBankAccount table (if this fails, account is still linked)
   *
   * 4. PROCESS MICRO-DEPOSIT ACCOUNTS
   *    For each pending-verification account:
   *    - Store record in PlaidBankAccount table with status='pending'
   *    - User will return later to verify via micro-deposit codes
   *
   * 5. SHOW RESULTS
   *    - Refresh account list from Highnote
   *    - Determine appropriate dialog (success/pending/duplicate/mixed/error)
   *    - Call success/pending callbacks with processed accounts
   *
   * Error Handling Strategy:
   * - Individual account failures don't stop processing of other accounts
   * - Auxiliary operations (duplicate check, record-keeping) fail gracefully
   * - All errors are logged to Sentry
   * - Users see appropriate feedback via dialogs
   */
  const handlePlaidSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      // Mark this token as consumed in Redux IMMEDIATELY to prevent reopening on remount
      dispatch(consumePlaidLinkToken());

      // Immediately show loading dialog
      setDialogState({ type: 'loading', message: 'Connecting your bank account...' });

      try {
        // Filter accounts to only supported types (checking/savings)
        const supportedAccounts = metadata.accounts.filter((account) =>
          SUPPORTED_ACCOUNT_TYPES.includes(account.subtype?.toLowerCase() ?? '')
        );

        if (supportedAccounts.length === 0) {
          throw new Error(
            'No supported accounts selected. Highnote only supports Checking and Savings accounts.'
          );
        }

        if (!integrationRef.current?.businessAccountHolderId || !integrationRef.current?.paidolId) {
          throw new Error('Missing business account holder ID or Paidol ID');
        }

        // Separate instantly verified accounts from pending micro-deposit accounts
        const instantAccounts = supportedAccounts.filter((a) => !isPendingVerification(a));
        const pendingAccounts = supportedAccounts.filter((a) => isPendingVerification(a));

        // Fetch FRESH existing accounts data for duplicate checking
        // This avoids stale closure issues with component remounts
        let freshPendingAccounts: PendingBankAccount[] = [];
        let freshActiveAccounts: Array<{ mask?: string; type?: string }> = [];

        try {
          // Fetch pending accounts using secure backend query (no access_token exposed)
          const pendingItems = await dispatch(
            fetchPlaidLinkedBankAccounts({
              paidolId: integrationRef.current!.paidolId,
              status: 'pending',
            })
          ).unwrap();
          const allPending = (pendingItems || []).map(toPendingBankAccount);

          // SEE-1445: an expired pending account can never be verified, so it must not
          // count as a duplicate. Leaving it in would permanently block the user from
          // re-adding the same account, which is exactly what the UI tells them to do.
          freshPendingAccounts = allPending.filter((p) => !isMicroDepositExpired(p.initiatedAt));
          await closeExpiredPendingAccounts(allPending.filter((p) => isMicroDepositExpired(p.initiatedAt)));

          // Fetch active accounts from Highnote
          const activeResult = await dispatch(getFinancialAccountDetails(integrationRef.current!)).unwrap();
          if (activeResult?.activeExternalBankAccounts) {
            freshActiveAccounts = activeResult.activeExternalBankAccounts.map(
              (acc: HNExternalFinancialBankAccount) => ({
                mask: acc.externalBankAccountDetails?.last4 ?? undefined,
                type: acc.externalBankAccountDetails?.type?.toLowerCase(),
              })
            );
          }
        } catch (fetchError) {
          captureException(fetchError);
          // INTENTIONAL: Continue with empty arrays rather than blocking the user.
          // Worst case: user adds a duplicate account, which is recoverable.
          // Better than preventing them from linking entirely.
        }

        const addedAccounts: HNExternalFinancialBankAccount[] = [];
        const createdPendingAccounts: PendingBankAccount[] = [];
        const successAccountsInfo: Array<{ name: string; mask: string; type: string }> = [];
        const pendingAccountsInfo: Array<{ name: string; mask: string; type: string }> = [];
        const alreadyLinkedAccounts: Array<{ name: string; mask: string; type: string }> = [];
        const alreadyPendingAccounts: Array<{ name: string; mask: string; type: string }> = [];
        const errors: string[] = [];

        // Process instantly verified accounts (current flow)
        for (const account of instantAccounts) {
          const accountLabel = `${account.name} (${account.subtype})`;

          // Check for duplicates using fresh data
          const duplicateCheck = isDuplicateAccount(account, freshPendingAccounts, freshActiveAccounts);
          if (duplicateCheck.isDuplicate) {
            const accountInfo = {
              name: account.name || 'Bank Account',
              mask: account.mask || '****',
              type: account.subtype || 'checking',
            };
            if (duplicateCheck.reason?.includes('pending')) {
              alreadyPendingAccounts.push(accountInfo);
            } else {
              alreadyLinkedAccounts.push(accountInfo);
            }
            continue;
          }

          try {
            setCurrentStep('exchanging');
            setDialogState({ type: 'loading', message: 'Verifying your account...' });

            // Exchange public_token for processor_token
            const processorTokenResult = await dispatch(
              getPlaidProcessorToken({
                publicToken,
                accountId: account.id,
                accountType: account.subtype?.toLowerCase() || 'checking',
              })
            ).unwrap();

            if (!processorTokenResult?.processor_token) {
              errors.push(`${accountLabel}: Failed to get processor token`);
              continue;
            }

            setCurrentStep('adding-to-hn');
            setDialogState({ type: 'loading', message: 'Adding account to your profile...' });

            // Add bank account to Highnote
            const newAccount = await dispatch(
              addExternalBankAccountViaPlaid({
                accountHolderId: integrationRef.current!.businessAccountHolderId,
                processorToken: processorTokenResult.processor_token,
              })
            ).unwrap();

            // Store the account record securely (access_token stays on backend)
            try {
              await storeLinkedAccountSecure(
                publicToken,
                account,
                integrationRef.current!.paidolId,
                'instant',
                newAccount.id
              );
            } catch (recordError) {
              // INTENTIONAL: The Highnote operation succeeded - the bank account IS linked.
              // If our internal record-keeping fails, we log to Sentry but don't fail the
              // user's flow. The account works; we just have incomplete tracking data.
              captureException(recordError);
            }

            addedAccounts.push(newAccount);
            successAccountsInfo.push({
              name: account.name || 'Bank Account',
              mask: account.mask || '****',
              type: account.subtype || 'checking',
            });
            logCreditAuditEvent(
              'bank_account.linked',
              {
                verificationMethod: 'instant',
                highnoteAccountId: newAccount.id,
                accountMask: account.mask ?? null,
                accountType: account.subtype ?? null,
              },
              {
                userId: user?.sub,
                paidolId: integrationRef.current?.paidolId,
                financialAccountId: integrationRef.current?.financialAccountId,
              }
            );
          } catch (accountError) {
            const errorMsg = accountError instanceof Error ? accountError.message : 'Unknown error';
            errors.push(`${accountLabel}: ${errorMsg}`);
            captureException(accountError);
          }
        }

        // Process pending micro-deposit accounts
        if (pendingAccounts.length > 0) {
          setCurrentStep('saving-pending');
          setDialogState({ type: 'loading', message: 'Setting up verification...' });

          for (const account of pendingAccounts) {
            // Check for duplicates using fresh data
            const duplicateCheck = isDuplicateAccount(account, freshPendingAccounts, freshActiveAccounts);
            if (duplicateCheck.isDuplicate) {
              const accountInfo = {
                name: account.name || 'Bank Account',
                mask: account.mask || '****',
                type: account.subtype || 'checking',
              };
              if (duplicateCheck.reason?.includes('pending')) {
                alreadyPendingAccounts.push(accountInfo);
              } else {
                alreadyLinkedAccounts.push(accountInfo);
              }
              continue;
            }

            try {
              // Store securely - access_token is exchanged and stored on backend
              const linkedAccount = await storeLinkedAccountSecure(
                publicToken,
                account,
                integrationRef.current!.paidolId,
                'micro_deposit'
              );
              // Convert to legacy format for callback
              createdPendingAccounts.push(toPendingBankAccount(linkedAccount));
              pendingAccountsInfo.push({
                name: account.name || 'Bank Account',
                mask: account.mask || '****',
                type: account.subtype || 'checking',
              });
              logCreditAuditEvent(
                'bank_account.linked_pending',
                {
                  verificationMethod: 'micro_deposit',
                  plaidLinkedId: linkedAccount.id,
                  accountMask: account.mask ?? null,
                  accountType: account.subtype ?? null,
                },
                {
                  userId: user?.sub,
                  paidolId: integrationRef.current?.paidolId,
                  financialAccountId: integrationRef.current?.financialAccountId,
                }
              );
            } catch (accountError) {
              const errorMsg = accountError instanceof Error ? accountError.message : 'Unknown error';
              errors.push(`${account.name} (${account.subtype}): ${errorMsg}`);
              captureException(accountError);
            }
          }
        }

        // Refresh account list
        if (integration) {
          await dispatch(getFinancialAccountDetails(integration));
        }

        // Handle results
        setCurrentStep('success');
        setIsLoading(false);

        // Determine which dialog to show
        if (successAccountsInfo.length > 0 && pendingAccountsInfo.length > 0) {
          // Mixed - some instant, some pending
          setDialogState({
            type: 'mixed',
            successAccounts: successAccountsInfo,
            pendingAccounts: pendingAccountsInfo,
          });
        } else if (successAccountsInfo.length > 0) {
          // All successful
          setDialogState({
            type: 'success',
            accounts: successAccountsInfo,
          });
        } else if (pendingAccountsInfo.length > 0) {
          // All pending
          setDialogState({
            type: 'pending',
            accounts: pendingAccountsInfo,
          });
        } else if (
          (alreadyLinkedAccounts.length > 0 || alreadyPendingAccounts.length > 0) &&
          errors.length === 0
        ) {
          // All accounts were duplicates
          if (alreadyLinkedAccounts.length > 0 && alreadyPendingAccounts.length === 0) {
            // All duplicates are already linked
            setDialogState({
              type: 'already_linked',
              accounts: alreadyLinkedAccounts,
            });
          } else if (alreadyPendingAccounts.length > 0 && alreadyLinkedAccounts.length === 0) {
            // All duplicates are already pending
            setDialogState({
              type: 'already_pending',
              accounts: alreadyPendingAccounts,
            });
          } else {
            // Mix of both - show already_linked as primary (they can go to pending accounts to verify)
            setDialogState({
              type: 'already_linked',
              accounts: [...alreadyLinkedAccounts, ...alreadyPendingAccounts],
            });
          }
        } else if (errors.length > 0) {
          // Other failures
          captureException(new Error(`Bank account linking failed: ${errors.join('; ')}`));
          setDialogState({ type: 'error' });
        }

        // Call callbacks
        if (addedAccounts.length > 0) {
          onSuccess?.(addedAccounts);
        }
        if (createdPendingAccounts.length > 0) {
          onPendingCreated?.(createdPendingAccounts);
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to add bank account');
        captureException(err);
        setCurrentStep('error');
        setIsLoading(false);
        setDialogState({ type: 'error', message: err.message });
        onError?.(err);
      }
    },
    [
      dispatch,
      onSuccess,
      onPendingCreated,
      onError,
      isDuplicateAccount,
      closeExpiredPendingAccounts,
      setDialogState,
    ]
  );

  // Handle Plaid Link exit (user closed without completing)
  const handlePlaidExit = useCallback(
    (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      // SEE-1445: this payload used to be discarded, so failures inside Plaid Link were
      // invisible to us. A user could fail repeatedly and nothing reached Sentry.
      if (error) {
        captureException(new Error(`Plaid Link exit: ${error.error_code} ${error.error_message}`), {
          extra: {
            errorType: error.error_type,
            errorCode: error.error_code,
            displayMessage: error.display_message,
            institutionId: metadata?.institution?.institution_id,
            institutionName: metadata?.institution?.name,
            requestId: metadata?.request_id,
            linkSessionId: metadata?.link_session_id,
            status: metadata?.status,
          },
        });
      }
      // Clear local token - this is what usePlaidLink actually sees
      setLocalPlaidToken(null);
      // Clear Redux state for cross-component consistency
      dispatch(
        setPlaidLinkToken({
          __typename: 'PlaidLinkToken',
          link_token: '',
          expiration: '',
          request_id: '',
        })
      );
      dispatch(setPlaidShouldOpenWhenReady(false));
      dispatch(consumePlaidLinkToken());
      // Reset local state
      setCurrentStep('idle');
      setIsLoading(false);
      onExit?.();
    },
    [onExit, dispatch]
  );

  // IMPORTANT: usePlaidLink needs a valid token to initialize properly
  // Using localPlaidToken (local state) instead of Redux directly for better control
  // over hook reinitialization after user exits
  const plaidConfig: PlaidLinkOptions = {
    token: localPlaidToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  };

  const { open: openPlaidModal, ready: isPlaidReady, error: plaidError } = usePlaidLink(plaidConfig);

  // Log Plaid errors to Sentry
  useEffect(() => {
    if (plaidError) {
      captureException(plaidError);
    }
  }, [plaidError]);

  // Refs for polling - updated synchronously during render
  const openPlaidModalRef = useRef(openPlaidModal);
  const isPlaidReadyRef = useRef(isPlaidReady);
  openPlaidModalRef.current = openPlaidModal;
  isPlaidReadyRef.current = isPlaidReady;

  // Track if we're currently polling to prevent double-opens
  const isPollingRef = useRef(false);

  // This effect ONLY handles the component remount case (onboarding stepper)
  // When component remounts with shouldOpenWhenReady already true in Redux
  useEffect(() => {
    // Only fire if:
    // 1. Redux says we should open
    // 2. We're not already polling (to prevent double-opens)
    // 3. usePlaidLink is ready
    // 4. We have a token
    if (shouldOpenWhenReady && !isPollingRef.current && isPlaidReady && localPlaidToken) {
      // Mark that we've handled it (stops any lingering polling from showing errors)
      isPollingRef.current = false;
      dispatch(setPlaidShouldOpenWhenReady(false));
      dispatch(consumePlaidLinkToken());
      setCurrentStep('plaid-open');
      setIsLoading(false);
      openPlaidModal();
    }
  }, [shouldOpenWhenReady, isPlaidReady, localPlaidToken, dispatch, openPlaidModal]);

  // Main function to initiate the Plaid flow
  const openPlaidLink = useCallback(async () => {
    if (!user?.sub) {
      setDialogState({ type: 'error', message: 'Please log in to add a bank account.' });
      return;
    }

    if (!integration?.businessAccountHolderId) {
      setDialogState({ type: 'error', message: 'Account not set up. Please contact support.' });
      return;
    }

    try {
      setIsLoading(true);
      setCurrentStep('loading-token');
      isPollingRef.current = true;

      // Step 1: Clear local token to force usePlaidLink to reset
      setLocalPlaidToken(null);

      // Step 2: Fetch a fresh token from the backend
      const redirectUri = window.location.origin + window.location.pathname;

      const result = await dispatch(
        getPlaidLinkToken({
          userId: user.sub,
          redirectUri,
        })
      ).unwrap();

      if (!result?.link_token) {
        throw new Error('Failed to get link token');
      }

      // Step 3: Set the new token in local state AND Redux (for remount case)
      setLocalPlaidToken(result.link_token);
      dispatch(setPlaidShouldOpenWhenReady(true));

      // Step 4: Poll for ready state using refs
      // This is the reliable way to open - doesn't depend on effect timing
      const maxAttempts = 50; // 5 seconds max
      let attempts = 0;

      const pollForReady = () => {
        attempts++;

        // Check if ready using ref (gets latest value)
        if (isPlaidReadyRef.current) {
          isPollingRef.current = false;
          dispatch(setPlaidShouldOpenWhenReady(false));
          dispatch(consumePlaidLinkToken());
          setCurrentStep('plaid-open');
          setIsLoading(false);
          openPlaidModalRef.current();
        } else if (attempts < maxAttempts) {
          setTimeout(pollForReady, 100);
        } else {
          // Timeout - but only show error if we're not on onboarding stepper
          // (the stepper causes component remounts which can leave orphaned polling)
          isPollingRef.current = false;
          dispatch(setPlaidShouldOpenWhenReady(false));
          setCurrentStep('error');
          setIsLoading(false);

          // Don't show error dialog on onboarding stepper - the modal likely opened via
          // the effect after a remount, and this is just an orphaned polling timeout
          const isOnboardingStepper = window.location.pathname.includes('/onboard');
          if (!isOnboardingStepper) {
            setDialogState({
              type: 'error',
              message: 'Failed to initialize bank linking. Please try again.',
            });
          }
        }
      };

      // Start polling after a tick
      setTimeout(pollForReady, 100);
    } catch (error) {
      isPollingRef.current = false;
      const err = error instanceof Error ? error : new Error('Failed to initialize Plaid');
      captureException(err);
      setCurrentStep('error');
      setIsLoading(false);
      setDialogState({ type: 'error', message: 'Failed to initialize bank linking. Please try again.' });
      onError?.(err);
    }
  }, [user?.sub, integration?.businessAccountHolderId, dispatch, onError, setDialogState]);

  return {
    openPlaidLink,
    isReady: !!user?.sub && !!integration?.businessAccountHolderId,
    isLoading,
    currentStep,
    dialogState,
    closeDialog,
  };
}

export default usePlaidBankAccountLink;
